import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Search, Filter, Calendar, ChevronDown, ChevronUp, AlertCircle, Info, X, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface SERPPosition {
  id: string;
  keyword: string;
  target_site: string;
  url: string;
  position: number;
  previous_position: number | null;
  search_volume: number;
  date: string;
  county: string | null;
  device: string;
  created_at: string;
}

interface KeywordRanking {
  keyword: string;
  current_position: number;
  previous_position: number | null;
  change: number;
  url: string;
  search_volume: number;
  device: string;
  county: string | null;
  trend: 'up' | 'down' | 'stable' | 'new';
  visibility_score: number;
}

export default function SERPTrackingPage() {
  const [serpData, setSerpData] = useState<SERPPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState<string>('all');
  const [selectedDevice, setSelectedDevice] = useState<string>('all');
  const [selectedCounty, setSelectedCounty] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'position' | 'change' | 'volume'>('position');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInstructions, setShowInstructions] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState({ current: 0, total: 0, status: '' });

  const targetSites = ['halesac', 'myccs', 'abrahamac'];
  const devices = ['desktop', 'mobile'];
  const counties = ['Broward', 'Palm Beach', 'Hillsborough', 'Pinellas'];

  useEffect(() => {
    fetchSERPData();
  }, []);

  async function fetchSERPData() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('serp_positions')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;
      setSerpData(data || []);
    } catch (error) {
      console.error('Error fetching SERP data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function refreshSERPData() {
    setIsRefreshing(true);
    setRefreshProgress({ current: 0, total: 0, status: 'Initializing...' });

    try {
      const dataforSeoLogin = import.meta.env.VITE_DATAFORSEO_LOGIN;
      const dataforSeoPassword = import.meta.env.VITE_DATAFORSEO_PASSWORD;

      if (!dataforSeoLogin || !dataforSeoPassword) {
        throw new Error('DataForSEO credentials not found in environment variables');
      }

      // Target domains configuration
      const targetDomains = [
        { brand: 'hales_ac', domain: 'halesac.com', county: 'pinellas' },
        { brand: 'hales_ac', domain: 'halesac.com', county: 'hillsborough' },
        { brand: 'climate_control_services', domain: 'my-ccs.com', county: 'palm_beach' },
        { brand: 'abraham_ac', domain: 'abrahamac.com', county: 'broward' }
      ];

      const auth = btoa(`${dataforSeoLogin}:${dataforSeoPassword}`);
      let processedCount = 0;

      for (const target of targetDomains) {
        setRefreshProgress({
          current: processedCount,
          total: targetDomains.length,
          status: `Processing ${target.domain} in ${target.county} county...`
        });

        // Get keywords for this county
        const { data: keywords, error: kwError } = await supabase
          .from('dataforseo_keywords')
          .select('keyword, location_code, search_volume')
          .eq('county', target.county)
          .gt('search_volume', 50)
          .order('search_volume', { ascending: false })
          .limit(5); // Limit to 5 keywords per county for faster collection

        if (kwError) throw kwError;

        if (!keywords || keywords.length === 0) {
          console.log(`No keywords found for ${target.county}`);
          continue;
        }

        // Get GPS grid center point
        const { data: gridPoints, error: gridError } = await supabase
          .from('dataforseo_maps_grid')
          .select('*')
          .eq('brand', target.brand)
          .eq('is_center_point', true)
          .limit(1)
          .single();

        if (gridError || !gridPoints) {
          console.log(`No GPS grid found for ${target.brand}`);
          continue;
        }

        const checkDate = new Date().toISOString().split('T')[0];

        // Track each keyword (both desktop and mobile)
        for (const keyword of keywords) {
          for (const device of ['desktop', 'mobile']) {
            setRefreshProgress({
              current: processedCount,
              total: targetDomains.length,
              status: `Tracking "${keyword.keyword}" on ${device} for ${target.domain}...`
            });

            // Call DataForSEO SERP API
            const postData = [{
              keyword: keyword.keyword,
              location_code: keyword.location_code,
              language_code: 'en',
              device: device,
              calculate_rectangles: true,
              latitude: gridPoints.latitude,
              longitude: gridPoints.longitude
            }];

            const response = await fetch('https://api.dataforseo.com/v3/serp/google/organic/live/advanced', {
              method: 'POST',
              headers: {
                'Authorization': `Basic ${auth}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(postData)
            });

            if (!response.ok) {
              console.error(`SERP API error: ${response.status}`);
              continue;
            }

            const result = await response.json();

            if (result.tasks?.[0]?.status_code !== 20000) {
              console.error(`SERP failed: ${result.tasks?.[0]?.status_message}`);
              continue;
            }

            const items = result.tasks[0].result?.[0]?.items || [];

            // Find target domain position
            let targetPosition = null;
            let targetUrl = null;

            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              if (item.type === 'organic') {
                const domain = extractDomain(item.url || '');
                if (domain === target.domain) {
                  targetPosition = item.rank_group || i + 1;
                  targetUrl = item.url;
                  break;
                }
              }
            }

            if (targetPosition) {
              // Get previous position
              const { data: prevRank } = await supabase
                .from('dataforseo_rank_tracking')
                .select('current_position')
                .eq('keyword', keyword.keyword)
                .eq('target_domain', target.domain)
                .eq('location_code', keyword.location_code)
                .eq('device', device)
                .order('check_date', { ascending: false })
                .limit(1)
                .single();

              const previousPosition = prevRank?.current_position || null;
              const positionChange = previousPosition ? (previousPosition - targetPosition) : 0;

              // Insert rank tracking data
              await supabase
                .from('dataforseo_rank_tracking')
                .upsert({
                  keyword: keyword.keyword,
                  target_domain: target.domain,
                  location_code: keyword.location_code,
                  language_code: 'en',
                  device: device,
                  latitude: gridPoints.latitude,
                  longitude: gridPoints.longitude,
                  current_position: targetPosition,
                  previous_position: previousPosition,
                  position_change: positionChange,
                  serp_item_type: 'organic',
                  url: targetUrl,
                  check_date: checkDate
                }, {
                  onConflict: 'keyword,target_domain,location_code,check_date,device'
                });
            }

            // Rate limiting
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        processedCount++;
      }

      setRefreshProgress({
        current: targetDomains.length,
        total: targetDomains.length,
        status: 'Collection complete! Refreshing data...'
      });

      // Refresh the displayed data
      await fetchSERPData();

      setRefreshProgress({
        current: targetDomains.length,
        total: targetDomains.length,
        status: 'Done!'
      });

    } catch (error) {
      console.error('Error refreshing SERP data:', error);
      setRefreshProgress({
        current: 0,
        total: 0,
        status: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setTimeout(() => setIsRefreshing(false), 2000);
    }
  }

  function extractDomain(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return '';
    }
  }

  // Aggregate latest positions by keyword
  const keywordRankings: KeywordRanking[] = React.useMemo(() => {
    const latestByKeyword = new Map<string, SERPPosition>();

    // Get latest position for each keyword
    serpData.forEach(row => {
      const key = `${row.keyword}_${row.target_site}_${row.device}_${row.county || 'all'}`;
      
      if (!latestByKeyword.has(key) || new Date(row.date) > new Date(latestByKeyword.get(key)!.date)) {
        latestByKeyword.set(key, row);
      }
    });

    // Convert to rankings with change calculation
    const rankings: KeywordRanking[] = [];
    latestByKeyword.forEach(pos => {
      // Apply filters
      if (selectedSite !== 'all' && pos.target_site !== selectedSite) return;
      if (selectedDevice !== 'all' && pos.device !== selectedDevice) return;
      if (selectedCounty !== 'all' && pos.county !== selectedCounty) return;
      if (searchTerm && !pos.keyword.toLowerCase().includes(searchTerm.toLowerCase())) return;

      const change = pos.previous_position ? pos.previous_position - pos.position : 0;
      let trend: 'up' | 'down' | 'stable' | 'new' = 'stable';
      
      if (pos.previous_position === null) {
        trend = 'new';
      } else if (change > 0) {
        trend = 'up';
      } else if (change < 0) {
        trend = 'down';
      }

      // Visibility score: higher for top positions, weighted by search volume
      const positionScore = Math.max(0, 100 - pos.position * 5);
      const volumeWeight = Math.log10(pos.search_volume + 1) / 5;
      const visibility_score = Math.round(positionScore * volumeWeight);

      rankings.push({
        keyword: pos.keyword,
        current_position: pos.position,
        previous_position: pos.previous_position,
        change,
        url: pos.url,
        search_volume: pos.search_volume,
        device: pos.device,
        county: pos.county,
        trend,
        visibility_score
      });
    });

    return rankings;
  }, [serpData, selectedSite, selectedDevice, selectedCounty, searchTerm]);

  // Sort rankings
  const sortedRankings = React.useMemo(() => {
    return [...keywordRankings].sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'position') {
        comparison = a.current_position - b.current_position;
      } else if (sortBy === 'change') {
        comparison = b.change - a.change; // Bigger improvements first
      } else if (sortBy === 'volume') {
        comparison = b.search_volume - a.search_volume;
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }, [keywordRankings, sortBy, sortOrder]);

  // Calculate summary stats
  const avgPosition = keywordRankings.length > 0 
    ? keywordRankings.reduce((sum, r) => sum + r.current_position, 0) / keywordRankings.length 
    : 0;
  const topThree = keywordRankings.filter(r => r.current_position <= 3).length;
  const topTen = keywordRankings.filter(r => r.current_position <= 10).length;
  const improvements = keywordRankings.filter(r => r.change > 0).length;
  const declines = keywordRankings.filter(r => r.change < 0).length;

  function toggleSort(field: 'position' | 'change' | 'volume') {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'position' ? 'asc' : 'desc');
    }
  }

  function getPositionColor(position: number) {
    if (position <= 3) return 'text-green-600 bg-green-50';
    if (position <= 10) return 'text-blue-600 bg-blue-50';
    if (position <= 20) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  }

  function getTrendIcon(trend: string, change: number) {
    if (trend === 'new') {
      return <span className="text-purple-600 text-xs font-semibold">NEW</span>;
    }
    if (trend === 'up') {
      return (
        <div className="flex items-center gap-1 text-green-600">
          <TrendingUp size={16} />
          <span className="text-sm font-semibold">+{Math.abs(change)}</span>
        </div>
      );
    }
    if (trend === 'down') {
      return (
        <div className="flex items-center gap-1 text-red-600">
          <TrendingDown size={16} />
          <span className="text-sm font-semibold">-{Math.abs(change)}</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1 text-gray-400">
        <Minus size={16} />
        <span className="text-sm">0</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Search className="text-blue-600" size={32} />
          <h1 className="text-3xl font-bold text-gray-900">SERP Position Tracking</h1>
        </div>
        <p className="text-gray-600">
          Monitor keyword rankings and track SEO performance across search engines
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Tracked Keywords</span>
            <Search className="text-blue-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{keywordRankings.length}</div>
          <div className="text-sm text-gray-500 mt-1">Active monitoring</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Avg Position</span>
            <TrendingUp className="text-purple-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{avgPosition > 0 ? avgPosition.toFixed(1) : 'N/A'}</div>
          <div className="text-sm text-gray-500 mt-1">Mean ranking</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Top 3 Rankings</span>
            <span className="text-2xl">🥇</span>
          </div>
          <div className="text-3xl font-bold text-green-600">{topThree}</div>
          <div className="text-sm text-gray-500 mt-1">{topTen} in top 10</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Improvements</span>
            <TrendingUp className="text-green-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-green-600">{improvements}</div>
          <div className="text-sm text-gray-500 mt-1">Position gains</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Declines</span>
            <TrendingDown className="text-red-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-red-600">{declines}</div>
          <div className="text-sm text-gray-500 mt-1">Position losses</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Target Site</label>
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Sites</option>
              {targetSites.map(site => (
                <option key={site} value={site}>{site}.com</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Device</label>
            <select
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Devices</option>
              {devices.map(device => (
                <option key={device} value={device}>{device.charAt(0).toUpperCase() + device.slice(1)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">County</label>
            <select
              value={selectedCounty}
              onChange={(e) => setSelectedCounty(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Counties</option>
              {counties.map(county => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'position' | 'change' | 'volume')}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="position">Position</option>
              <option value="change">Change</option>
              <option value="volume">Search Volume</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Keywords</label>
            <input
              type="text"
              placeholder="Filter keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="mb-8">
        <button
          onClick={refreshSERPData}
          disabled={isRefreshing}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
            isRefreshing
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg'
          }`}
        >
          <RefreshCw className={isRefreshing ? 'animate-spin' : ''} size={20} />
          {isRefreshing ? 'Collecting SERP Data...' : 'Refresh SERP Data'}
        </button>

        {isRefreshing && refreshProgress.total > 0 && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">
                {refreshProgress.status}
              </span>
              <span className="text-sm font-semibold text-blue-600">
                {refreshProgress.current} / {refreshProgress.total}
              </span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${refreshProgress.total > 0 ? (refreshProgress.current / refreshProgress.total) * 100 : 0}%`
                }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Rankings Table */}
      {sortedRankings.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No SERP Data Available</h3>
          <p className="text-gray-600 mb-6">
            Click the button above to collect keyword ranking data from DataForSEO
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Keyword
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleSort('position')}
                  >
                    <div className="flex items-center gap-1">
                      Position
                      {sortBy === 'position' && (
                        sortOrder === 'desc' ? <ChevronDown size={16} /> : <ChevronUp size={16} />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleSort('change')}
                  >
                    <div className="flex items-center gap-1">
                      Change
                      {sortBy === 'change' && (
                        sortOrder === 'desc' ? <ChevronDown size={16} /> : <ChevronUp size={16} />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleSort('volume')}
                  >
                    <div className="flex items-center gap-1">
                      Search Volume
                      {sortBy === 'volume' && (
                        sortOrder === 'desc' ? <ChevronDown size={16} /> : <ChevronUp size={16} />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    URL
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device / County
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Visibility Score
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedRankings.map((ranking, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Search size={16} className="text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{ranking.keyword}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-3 py-1 text-sm font-bold rounded-full ${getPositionColor(ranking.current_position)}`}>
                        #{ranking.current_position}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {getTrendIcon(ranking.trend, ranking.change)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-semibold text-gray-900">
                        {ranking.search_volume.toLocaleString()}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">/mo</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs text-gray-600 truncate block max-w-xs" title={ranking.url}>
                        {ranking.url}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                          {ranking.device}
                        </span>
                        {ranking.county && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded">
                            {ranking.county}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${Math.min(ranking.visibility_score, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-semibold text-gray-700">
                          {ranking.visibility_score}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Instructions Panel */}
      {showInstructions && (
        <div className="mt-8 bg-orange-50 border border-orange-200 rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <Info className="w-5 h-5 text-orange-600 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-orange-900">How to Use SERP Tracking</h3>
            </div>
            <button
              onClick={() => setShowInstructions(false)}
              className="text-orange-600 hover:text-orange-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4 text-sm text-orange-900">
            <div>
              <h4 className="font-semibold mb-2">🔍 Understanding SERP Positions</h4>
              <p className="text-orange-800">
                Track your website's keyword rankings in Google search results. Monitor position changes over time to measure SEO performance, identify opportunities, and respond to algorithm updates quickly.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">📊 Key Metrics</h4>
              <div className="space-y-2 text-orange-800">
                <div><strong>Position:</strong> Your ranking in search results (1-100+). Top 3 positions get 75% of clicks</div>
                <div><strong>Change:</strong> Position movement since last check. Positive = ranking improved (moving up)</div>
                <div><strong>Search Volume:</strong> Monthly searches for the keyword. Higher volume = more potential traffic</div>
                <div><strong>Visibility Score:</strong> Combines position and volume: (100 - position × 5) × log₁₀(volume + 1) / 5</div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">🎯 Position Ranges</h4>
              <div className="grid grid-cols-2 gap-2 text-orange-800 text-xs">
                <div><strong className="text-green-600">Top 3:</strong> Premium positions, highest CTR (click-through rate)</div>
                <div><strong className="text-blue-600">4-10:</strong> First page, good visibility, optimization target</div>
                <div><strong className="text-yellow-600">11-20:</strong> Second page, moderate visibility, quick win opportunities</div>
                <div><strong className="text-gray-600">21+:</strong> Low visibility, needs significant SEO work</div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">📱 Device & Location Tracking</h4>
              <ul className="list-disc list-inside space-y-1 text-orange-800">
                <li><strong>Desktop vs Mobile:</strong> Rankings differ by device. Mobile-first indexing makes mobile crucial</li>
                <li><strong>County Targeting:</strong> Local rankings vary by location. Track specific counties for local SEO</li>
                <li><strong>NEW Badge:</strong> Recently tracked keywords with no historical data yet</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">⚡ Using the Dashboard</h4>
              <ul className="list-disc list-inside space-y-1 text-orange-800">
                <li><strong>Filters:</strong> Segment by site, device, county to analyze specific areas</li>
                <li><strong>Sort:</strong> Click column headers to sort by position, change, or search volume</li>
                <li><strong>Search:</strong> Filter to specific keywords to track priority terms</li>
                <li><strong>Trends:</strong> Green (up arrow) = improved, Red (down) = declined, Gray (minus) = stable</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">💡 Strategy Tips</h4>
              <div className="grid grid-cols-2 gap-2 text-orange-800 text-xs">
                <div><strong>Quick Wins:</strong> Target keywords at positions 4-10 to reach page 1 top 3</div>
                <div><strong>Volume Priority:</strong> Focus on high-volume keywords with positions 11-20</div>
                <div><strong>Defend Top 3:</strong> Monitor closely and maintain positions 1-3 with fresh content</div>
                <div><strong>Track Competitors:</strong> Watch for position losses that may indicate competitor activity</div>
              </div>
            </div>

            <div className="bg-orange-100 rounded p-3 mt-4">
              <p className="text-orange-900">
                <strong>💡 Pro Tip:</strong> Schedule daily automated tracking using the in-app "Refresh SERP Data" flow via a backend cron or job runner. Position changes happen gradually—focus on trends over time rather than daily fluctuations. A position 4→3 move can double your traffic!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
